/**
 * EXPERT ROOMS (EXPERT-ROOMS.md §4, E8 X1) — in the room release dialog of an EXPERT NETWORK: deploy it as a team of
 * rooms (this room becomes the Lead + one linked room per Team), or — when the team already runs — add the Teams
 * that don't have a room yet. One team per STAGE (EXPERT-ROOMS §15): Training (dev) — always; QA / Production — once a
 * release has been promoted to it (that team runs exactly the release, locked). Additive: a plain network never shows it.
 */
import React from 'react';
import { missingTeams } from '@stepflow/ui-shared/services/expertDeployment';

const STAGE_LABEL = { dev: 'Training (dev)', qa: 'QA', prod: 'Production' };

export default function ExpertTeamDeployOption({ network, checked, onChange, progress, disabled, stage = 'dev', onStage }) {
  if (!network?.expertNetwork) return null;
  const promoted = ((network.expertReleases || {}).stages || []).filter((s) => s.stage !== 'dev' && s.version > 0);
  const options = ['dev', ...promoted.map((s) => s.stage)];
  const dep = (network.expertDeployments || {})[stage] || (stage === 'dev' ? network.expertDeployment : null) || null;
  const missing = missingTeams(dep);
  const steps = Array.isArray(progress) ? progress : [];
  return (
    <fieldset className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-3 text-sm" data-testid="expert-team-deploy">
      <legend className="px-1 font-medium text-gray-900">Expert team</legend>
      {options.length > 1 && onStage ? (
        <label className="mb-1 flex items-center gap-2 text-xs text-gray-700">
          Stage
          <select value={stage} disabled={disabled} onChange={(e) => onStage(e.target.value)} data-testid="expert-team-stage"
            className="rounded border border-gray-300 px-1.5 py-0.5 text-xs">
            {options.map((o) => {
              const v = promoted.find((p) => p.stage === o)?.version;
              return <option key={o} value={o}>{STAGE_LABEL[o]}{v ? ` — release v${v}` : ''}</option>;
            })}
          </select>
        </label>
      ) : null}
      {dep?.leadPoolId ? (
        <p className="text-xs text-gray-700" data-testid="expert-team-existing">
          This Expert Network already runs as a team — Lead room <span className="font-mono">{dep.leadPoolId}</span>,
          {' '}{Object.keys(dep.departments || {}).length} of {(dep.teams || []).length} department rooms.
        </p>
      ) : null}
      {!dep?.leadPoolId || missing.length ? (
        <label className="mt-1 flex items-start gap-2 text-xs text-gray-800">
          <input type="checkbox" checked={!!checked} disabled={disabled} data-testid="expert-team-toggle"
            onChange={(e) => onChange?.(e.target.checked)} />
          <span>
            {dep?.leadPoolId
              ? `Add the missing department rooms (${missing.join(', ')}) to the team.`
              : stage === 'dev'
                ? 'Deploy as an expert team — this room becomes the Lead, plus one linked room per Team (department). This is the training team: your own workspace asks it.'
                : `Deploy the ${STAGE_LABEL[stage]} team — it runs the promoted release exactly and is locked${stage === 'prod' ? '; other workspaces of the company are answered by it' : ''}.`}
          </span>
        </label>
      ) : null}
      {steps.length ? (
        <ul className="mt-2 space-y-0.5 text-[11px]" data-testid="expert-team-progress">
          {steps.map((s) => (
            <li key={s.team || '__lead'} className={s.status === 'failed' ? 'text-rose-700' : 'text-gray-700'}>
              {s.team || 'Lead room'} — {s.status}{s.message ? ` (${s.message})` : ''}
            </li>
          ))}
        </ul>
      ) : null}
    </fieldset>
  );
}
